import type { FetchLike } from './config-fetch.js';
import type { ConsentEventPayload } from './types.js';
import type {
  ConsentEventQueue,
  QueuedConsentEvent,
} from './event-queue.js';

export const EVENT_DELIVERY_TIMEOUT_MS = 5_000;
export const EVENT_RETRY_BASE_DELAY_MS = 1_000;
export const EVENT_RETRY_MAX_DELAY_MS = 5 * 60 * 1_000;

export type ConsentEventDeliveryResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly retryable: boolean;
      readonly status?: number;
      readonly retryAfterMs?: number;
    };

export interface SendConsentEventOptions {
  readonly eventUrl: string;
  readonly payload: ConsentEventPayload;
  readonly timeoutMs?: number;
  readonly allowInsecureLocalhost?: boolean;
  readonly fetch?: FetchLike;
}

function isLocalhost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]'
  );
}

function parseEventUrl(rawUrl: string, allowInsecureLocalhost: boolean): URL {
  const url = new URL(rawUrl);
  if (url.username !== '' || url.password !== '') {
    throw new TypeError('Event URL must not contain credentials.');
  }
  if (url.protocol === 'https:') return url;
  if (
    url.protocol === 'http:' &&
    allowInsecureLocalhost &&
    isLocalhost(url.hostname)
  ) {
    return url;
  }
  throw new TypeError('Event URL must use HTTPS except on explicit localhost development.');
}

export function validateConsentEventUrl(
  rawUrl: string,
  allowInsecureLocalhost = false,
): void {
  parseEventUrl(rawUrl, allowInsecureLocalhost);
}

function retryAfterMilliseconds(value: string | null, now: number): number | undefined {
  if (value === null) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1_000, EVENT_RETRY_MAX_DELAY_MS);
  }
  const date = Date.parse(value);
  if (Number.isNaN(date)) return undefined;
  return Math.min(Math.max(0, date - now), EVENT_RETRY_MAX_DELAY_MS);
}

export async function sendConsentEvent(
  options: SendConsentEventOptions,
): Promise<ConsentEventDeliveryResult> {
  const timeoutMs = options.timeoutMs ?? EVENT_DELIVERY_TIMEOUT_MS;
  if (!Number.isSafeInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 60_000) {
    throw new TypeError('Event delivery timeout is invalid.');
  }
  const url = parseEventUrl(
    options.eventUrl,
    options.allowInsecureLocalhost ?? false,
  );
  const fetchImplementation = options.fetch ?? globalThis.fetch;
  if (typeof fetchImplementation !== 'function') {
    throw new TypeError('Event delivery requires Fetch API.');
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetchImplementation(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options.payload),
      credentials: 'omit',
      redirect: 'manual',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
    });
  } catch {
    return { ok: false, retryable: true };
  } finally {
    clearTimeout(timer);
  }

  if (
    response.redirected ||
    response.type === 'opaqueredirect' ||
    (response.status >= 300 && response.status < 400)
  ) {
    return { ok: false, retryable: false, status: response.status };
  }
  if (response.ok) return { ok: true };

  const retryable =
    response.status === 408 ||
    response.status === 425 ||
    response.status === 429 ||
    response.status >= 500;
  const retryAfterMs = retryable
    ? retryAfterMilliseconds(response.headers.get('retry-after'), Date.now())
    : undefined;
  return {
    ok: false,
    retryable,
    status: response.status,
    ...(retryAfterMs === undefined ? {} : { retryAfterMs }),
  };
}

interface LockManagerLike {
  request<T>(
    name: string,
    options: { readonly ifAvailable: true },
    callback: (lock: unknown | null) => Promise<T>,
  ): Promise<T>;
}

export interface ConsentEventDispatcherOptions {
  readonly queue: ConsentEventQueue;
  readonly eventUrl: string;
  readonly timeoutMs?: number;
  readonly allowInsecureLocalhost?: boolean;
  readonly fetch?: FetchLike;
  readonly onlineTarget?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
  readonly lockManager?: LockManagerLike;
  readonly now?: () => Date;
  readonly onCompleted?: (clientEventId: string) => void;
  readonly onFailed?: (clientEventId: string, retryable: boolean) => void;
  readonly onError?: (error: unknown) => void;
}

export interface ConsentEventDispatcher {
  enqueue(payload: ConsentEventPayload): void;
  flush(): Promise<void>;
  start(): void;
  stop(): void;
}

function retryDelay(attempts: number): number {
  return Math.min(
    EVENT_RETRY_BASE_DELAY_MS * 2 ** Math.min(attempts, 18),
    EVENT_RETRY_MAX_DELAY_MS,
  );
}

export function createConsentEventDispatcher(
  options: ConsentEventDispatcherOptions,
): ConsentEventDispatcher {
  let started = false;
  let flushPromise: Promise<void> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  const onlineTarget = options.onlineTarget ?? globalThis;
  const lockManager =
    options.lockManager ??
    (typeof navigator !== 'undefined'
      ? (navigator.locks as LockManagerLike | undefined)
      : undefined);
  const now = options.now ?? (() => new Date());

  function reportError(error: unknown): void {
    try {
      options.onError?.(error);
    } catch {
      // Diagnostics cannot change delivery behavior.
    }
  }

  function completed(clientEventId: string): void {
    try {
      options.onCompleted?.(clientEventId);
    } catch (error) {
      reportError(error);
    }
  }

  function failed(clientEventId: string, retryable: boolean): void {
    try {
      options.onFailed?.(clientEventId, retryable);
    } catch (error) {
      reportError(error);
    }
  }

  function schedule(delayMs: number): void {
    if (!started) return;
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void flush().catch(reportError);
    }, Math.max(0, Math.min(delayMs, EVENT_RETRY_MAX_DELAY_MS)));
  }

  async function drain(): Promise<void> {
    while (started) {
      let head: QueuedConsentEvent | undefined;
      try {
        head = options.queue.read()[0];
      } catch (error) {
        reportError(error);
        return;
      }
      if (!head) return;

      const currentMs = now().getTime();
      if (Number.isNaN(currentMs)) {
        reportError(new TypeError('Consent event dispatcher clock is invalid.'));
        return;
      }
      if (head.nextAttemptAt > currentMs) {
        schedule(head.nextAttemptAt - currentMs);
        return;
      }

      let result: ConsentEventDeliveryResult;
      try {
        result = await sendConsentEvent({
          eventUrl: options.eventUrl,
          payload: head.payload,
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          ...(options.allowInsecureLocalhost === undefined
            ? {}
            : { allowInsecureLocalhost: options.allowInsecureLocalhost }),
          ...(options.fetch === undefined ? {} : { fetch: options.fetch }),
        });
      } catch (error) {
        reportError(error);
        failed(head.payload.clientEventId, false);
        return;
      }

      if (result.ok) {
        try {
          options.queue.removeHead(head.payload.clientEventId);
        } catch (error) {
          reportError(error);
          return;
        }
        completed(head.payload.clientEventId);
        continue;
      }

      failed(head.payload.clientEventId, result.retryable);
      if (!result.retryable) {
        try {
          options.queue.removeHead(head.payload.clientEventId);
        } catch (error) {
          reportError(error);
          return;
        }
        continue;
      }

      const attempts = head.attempts + 1;
      const delay = result.retryAfterMs ?? retryDelay(attempts - 1);
      try {
        options.queue.replaceHead({
          ...head,
          attempts,
          nextAttemptAt: currentMs + delay,
        });
      } catch (error) {
        reportError(error);
        return;
      }
      schedule(delay);
      return;
    }
  }

  async function runWithLock(): Promise<void> {
    if (!lockManager) {
      await drain();
      return;
    }
    await lockManager.request(
      'goksima-consent-event-delivery',
      { ifAvailable: true },
      async (lock) => {
        if (lock) await drain();
      },
    );
  }

  function flush(): Promise<void> {
    if (flushPromise) return flushPromise;
    const pending = runWithLock().finally(() => {
      if (flushPromise === pending) flushPromise = null;
    });
    flushPromise = pending;
    return pending;
  }

  const onlineListener = (): void => {
    void flush().catch(reportError);
  };

  return Object.freeze({
    enqueue(payload: ConsentEventPayload): void {
      try {
        options.queue.enqueue(payload);
      } catch (error) {
        reportError(error);
        failed(payload.clientEventId, false);
        return;
      }
      if (started) void flush().catch(reportError);
    },

    flush,

    start(): void {
      if (started) return;
      started = true;
      onlineTarget.addEventListener('online', onlineListener);
      void flush().catch(reportError);
    },

    stop(): void {
      if (!started) return;
      started = false;
      onlineTarget.removeEventListener('online', onlineListener);
      if (retryTimer !== null) clearTimeout(retryTimer);
      retryTimer = null;
    },
  });
}
